import { IconButton, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import EditIcon from "resources/images/EditIcon.png";
import Button from "components/basic/button";
import Typography from "components/basic/typography";
import UploadFile from "components/basic/uploadFile";

export interface InlineFileEditRowProps {
  label: string;
  file?: File | null;
  url?: string | null;
  onSave: (file: File) => void;
  uploadLabel?: string;
}

const isPdfSource = (file?: File | null, url?: string | null) => {
  if (file) {
    return file.type === "application/pdf";
  }

  if (url) {
    const path = decodeURIComponent(url).split("?")[0];
    return path.toLowerCase().endsWith(".pdf");
  }

  return false;
};

const FilePreview: React.FC<{
  previewUrl: string;
  isPdf: boolean;
  label: string;
}> = ({ previewUrl, isPdf, label }) => {
  const theme = useTheme();

  return (
    <div
      style={{
        borderRadius: theme.shape.borderRadius,
        overflow: "hidden",
        border: `1px solid ${theme.palette.grey[300]}`,
        width: 240,
        maxWidth: "100%",
      }}
    >
      {isPdf ? (
        <iframe
          src={previewUrl}
          title={`${label} preview`}
          style={{ width: "100%", height: 180, border: "none" }}
        />
      ) : (
        <img
          src={previewUrl}
          alt={`${label} preview`}
          style={{
            width: "100%",
            display: "block",
            maxHeight: 160,
            objectFit: "contain",
          }}
        />
      )}
    </div>
  );
};

const InlineFileEditRow: React.FC<InlineFileEditRowProps> = ({
  label,
  file,
  url,
  onSave,
  uploadLabel = "Upload File",
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [draftFile, setDraftFile] = useState<File | null>(null);
  const [objectUrl, setObjectUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!file) {
      setObjectUrl(null);
      return;
    }
    const generated = URL.createObjectURL(file);
    setObjectUrl(generated);
    return () => URL.revokeObjectURL(generated);
  }, [file]);

  const previewUrl = objectUrl ?? url ?? null;
  const isPdf = isPdfSource(file, url);

  const handleEdit = () => {
    setDraftFile(file ?? null);
    setEditing(true);
  };

  const handleCancel = () => {
    setDraftFile(null);
    setEditing(false);
  };

  const handleSave = () => {
    if (draftFile) {
      onSave(draftFile);
    }
    setEditing(false);
  };

  return (
    <div
      style={{
        borderBottom: `1px solid ${theme.palette.divider}`,
        padding: `${theme.spacing(0.75)} 0`,
      }}
    >
      {!editing ? (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            // The preview drops under its label on phones instead of overflowing.
            flexWrap: "wrap",
            gap: theme.spacing(1),
          }}
        >
          <Typography variant="body2" style={{ opacity: 0.7 }}>
            {label}
          </Typography>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: theme.spacing(0.5),
              marginLeft: "auto",
              maxWidth: "100%",
            }}
          >
            {previewUrl ? (
              <FilePreview
                previewUrl={previewUrl}
                isPdf={isPdf}
                label={label}
              />
            ) : (
              <Typography variant="body2" style={{ fontWeight: "bold" }}>
                —
              </Typography>
            )}

            <IconButton size="small" onClick={handleEdit}>
              <img src={EditIcon} alt={t("ui.edit")} width={15} height={15} />
            </IconButton>
          </div>
        </div>
      ) : (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: theme.spacing(0.5),
          }}
        >
          <Typography variant="body2" style={{ opacity: 0.7 }}>
            {label}
          </Typography>

          {!draftFile && previewUrl && (
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <FilePreview
                previewUrl={previewUrl}
                isPdf={isPdf}
                label={label}
              />
            </div>
          )}

          <UploadFile
            file={draftFile}
            onChange={setDraftFile}
            label={uploadLabel}
          />

          <div style={{ display: "flex", gap: theme.spacing(1) }}>
            <Button
              size="small"
              variant="contained"
              color="warning"
              onClick={handleSave}
              style={{ color: "white", padding: `0 ${theme.spacing(2)}` }}
              disabled={!draftFile}
            >
              {t("ui.save")}
            </Button>

            <Button size="small" variant="text" onClick={handleCancel}>
              {t("ui.cancel")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default InlineFileEditRow;
